/**
 * Weidner Via6 — Shared page-hero body.
 *
 * Internal helper used by every per-page hero slot in this theme
 * (`FloorPlansHero`, `AmenitiesHero`, `VirtualTourHero`, ...). It is
 * NOT registered as a slot itself and has no `contentSchema` — the
 * filename prefix `_` marks it as a private rendering helper.
 *
 * Each public *Hero.tsx component owns its slot/schema and the literal
 * schema-lint anchors, then calls this body with its dynamic paths
 * and defaults. Sharing the visual here means a left-aligned hero
 * tweak only has to be made in one place even though every sub-page
 * keeps a distinct slot name in the registry.
 *
 * Visual contract (the user-facing requirement):
 *   - Background image fills the section (slides behind the fixed nav).
 *   - Subtle left-weighted dark scrim so the title stays readable on
 *     any photo without flattening the right side of the image.
 *   - Page title is left-aligned, anchored to the bottom-left of the
 *     section. No CTA, no blurb — just the title.
 *   - Total section height includes the fixed nav offset so the visible
 *     portion below the nav is roughly 240px (mobile) / 280px (desktop).
 *     The SpecialOfferBanner publishes `--offer-banner-height` on :root
 *     when active; we add it to the offset so the banner stacks cleanly.
 */

import { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import {
  useGallery,
  usePropertyField,
  useSlotGroup,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';

export interface PageImageHeroBodyProps {
  /** Schema key for the editable page title (e.g. "floorplanshero.title"). */
  titlePath: string;
  /** Schema key for the editable background slot group. */
  bgPath: string;
  /** Literal slot-group definition matching the per-slot schema. */
  bgDef: SlotGroupDefinition;
  /** Default title shown until the editor sets one. */
  defaultTitle: string;
  /**
   * Keywords used to score photos in `photo_gallery` so a relevant image
   * is shown by default (e.g. `['amenity', 'pool']` for AmenitiesHero).
   */
  keywords?: string[];
  /**
   * Stable salt for the deterministic fallback when no photo matches any
   * keyword (`pickHeroImage` hashes this to pick a slot in the gallery).
   * Use the canonical slot name, e.g. `'floorplanshero'`.
   */
  pageHint: string;
  /** Alt text default when neither the slot nor the photo carry one. */
  altText?: string;
}

export function PageImageHeroBody({
  titlePath,
  bgPath,
  bgDef,
  defaultTitle,
  keywords = [],
  pageHint,
  altText = 'Page banner',
}: PageImageHeroBodyProps) {
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');
  const bgImage = useSlotGroup(bgPath, bgDef);

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  // Resolution order matches rise's PageHero: explicit editor override →
  // keyword-matched gallery photo → featured property image → first
  // gallery photo → null (renders a flat brand-tone block).
  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  return (
    <section className="page-image-hero relative w-full overflow-hidden">
      {/* Background image fills the entire section, including the strip
          behind the fixed nav. The nav's own background covers that strip
          so the visible image area starts where the nav ends. */}
      <EditableSlotGroup
        groupPath={bgPath}
        definition={bgDef}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            url={displayImage}
            alt={bgImage.alt || altText}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          // Brand-tone fallback if no photo is available yet. Uses the
          // same `--vibe-color-text` token rise's PageHero falls back to
          // so the empty state stays on-brand instead of going pure black.
          <div className="h-full w-full bg-[var(--vibe-color-text)]" />
        )}
        {/* Left-weighted dark scrim so white title text stays legible
            without flattening the right side of the photo. */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/15" />
      </EditableSlotGroup>

      {/* Content layer fills the section and vertically centers the
          title in the *visible* hero area (the part below the fixed
          nav). We do that by pushing the flex column down by the nav
          offset via `padding-top`, then `justify-center` centers the
          remaining content in the area that follows. The title is the
          only thing rendered — no eyebrow, blurb, or CTA — to match
          the "just the page title, left-aligned" brief. */}
      <div className="page-image-hero-content relative z-10 flex h-full w-full flex-col justify-center">
        <div className="mx-auto w-full max-w-6xl px-6 sm:px-8 lg:px-12">
          <RichTextContent
            path={titlePath}
            defaultValue={defaultTitle}
            as="h1"
            className="page-image-hero-title font-heading text-4xl sm:text-5xl md:text-6xl font-normal uppercase tracking-wider leading-tight text-left text-white drop-shadow-lg [&_strong]:font-normal [&_b]:font-normal"
          />
        </div>
      </div>

      <style>{`
        /* Total section height = fixed-nav offset + visible hero band.
           Mobile: 80px nav + ~320px visible. Desktop: 140px nav +
           ~380px visible. The SpecialOfferBanner height is added on
           top when active so the banner doesn't eat into the visible
           image area. */
        .page-image-hero {
          height: calc(400px + var(--offer-banner-height, 0px));
        }
        @media (min-width: 1024px) {
          .page-image-hero {
            height: calc(520px + var(--offer-banner-height, 0px));
          }
        }
        /* Push the centered content down by the fixed-nav (+ live offer
           banner) height. Combined with the column's \`justify-center\`,
           this vertically centers the title in the *visible* hero area
           (below the nav) instead of the geometric center of the whole
           section — so the title reads as ~50% of the visible image,
           on the left, regardless of whether the offer banner is active. */
        .page-image-hero-content {
          padding-top: calc(80px + var(--offer-banner-height, 0px));
        }
        @media (min-width: 1024px) {
          .page-image-hero-content {
            padding-top: calc(140px + var(--offer-banner-height, 0px));
          }
        }
      `}</style>
    </section>
  );
}

export default PageImageHeroBody;
